/**
 * Shared connect-popup engine (integrations-page UX overhaul, 2026-08-18).
 *
 * ONE popup/mint/poll implementation for every surface that connects a
 * Composio-managed app: the Integrations page, the in-chat connect card, and
 * onboarding. The two previous copies (Onboarding.connectAppInFlow and
 * connectApp.launchConnectFlow) drifted independently; the mechanics now live
 * here and the surfaces keep only their own UI side effects via adapters.
 *
 * Reliability contract (carried over from task #53 and hardened):
 *  - the popup opens SYNCHRONOUSLY inside the click's call stack and is
 *    navigated once the OAuth URL mints — awaiting the mint first ran
 *    window.open outside the user gesture and browsers silently blocked it;
 *  - `popup.opener = null` manually (the `noopener` feature flag would null
 *    the handle needed for the later navigate);
 *  - popup blocked → one direct retry with the minted URL (the gesture may
 *    still be transient-active), then a 'blocked' outcome carrying the URL so
 *    the surface renders an `<a target="prest0n-connect">` fallback — a link
 *    click reuses the same window name and is never blocked;
 *  - completion detection is polling only (no postMessage — the popup is
 *    opener-severed; the localStorage handshake in ComposioClient is the one
 *    shared channel), TARGET-AWARE: an organization-target connect polls the
 *    organization set (it never appears in the personal set);
 *  - on ACTIVE the engine closes the popup, invalidates the connected-apps
 *    cache, and fires the capability reconcile — one place instead of three.
 */

import {
  connectToolkit,
  getAccountsTwoSets,
  getConnectedAccounts,
  supportsFolderScope,
  markConnectPopupLaunch,
  markAddAccountLaunch,
} from './ComposioClient';
import type { ConnectInitField, ConnectToolkitHelp } from './ComposioClient';
import { peekConnectLane, laneIsDirect } from './connectLane';
import type { ConnectLane } from './connectLane';
import { firstPartyAppOf } from './firstPartyApps';
import { invalidateConnectedAppsCache } from '../threadRecommendations/connectedAppsStore';

// Poll cadence — the historical 3s × 40 (2 minutes), shared by all surfaces.
export const CONNECT_POLL_INTERVAL_MS = 3000;
export const CONNECT_POLL_MAX_TICKS = 40;

/** Default OAuth window geometry. Pinned top-left — ComposioConnectCoach's
 *  arrow points at this spot (task #47); provider consent pages are narrow. */
export const CONNECT_POPUP_FEATURES = 'width=560,height=720,left=60,top=60';

/** The shared OAuth window name. A link with target=CONNECT_POPUP_WINDOW_NAME
 *  reuses (and un-blocks) the same window — the manual fallback contract. */
export const CONNECT_POPUP_WINDOW_NAME = 'prest0n-connect';

// ============================================================================
// Funnel telemetry (house pattern: typed port, no-op default, swappable sink)
// ============================================================================

/**
 * Every funnel event, as a RUNTIME tuple (2026-09-02 review): the server's
 * connect-funnel sink turns these names into Firestore field keys, so an
 * open charset would let any signed-in caller grow the day row without
 * bound until it passes Firestore's 1 MiB limit and the whole day's
 * telemetry dies for everyone. The allowlist is the gate; the type is
 * derived from it, so a new event cannot exist without joining it.
 * functions/src/composio/connectFunnelEvents.ts carries the server twin,
 * byte-pinned by tests/onboarding/connectFunnelAllowlist.test.ts.
 */
export const CONNECT_FUNNEL_EVENTS = [
  'started',
  'popup_blocked',
  'needs_setup',
  'denied',
  'error',
  'timeout',
  'aborted',
  'connected',
  // custom (browser-signin) lane events share the funnel:
  'session_start_failed',
  'no_first_frame',
  'deep_link_minted',
  'custom_connected',
  // account-VM base ladder + computer-use sign-in pilot (2026-08-18):
  'session_on_account_vm',
  'account_vm_fallback',
  'pilot_started',
  'pilot_arrived',
  'pilot_stuck',
  'pilot_unavailable',
  // redirect-proof hand-off (2026-08-22 Fitbit incident): the destination
  // itself redirected off the app / the watch re-drove the pilot.
  'pilot_wrong_destination',
  'pilot_redriven',
  // wake-aware connect (2026-08-19): the sign-in surface wakes/provisions
  // the workstation instead of silently signing into the wrong machine.
  'waking_shown',
  'wake_completed',
  'wake_stalled',
  'workspace_provision_kicked',
  'workspace_unavailable',
  // detection-assisted completion + custom wow moment (2026-08-19):
  'signin_verified',
  'custom_done_card',
  // 2026-08-22 owner directive: sustained high-confidence verification
  // completes the connection without waiting for the tap.
  'signin_auto_promoted',
  // destination-first journey (2026-08-20 Kalshi repro): where the journey
  // resolved its sign-in URL from, and the deterministic retarget belt.
  'destination_researched',
  'destination_retargeted',
  'guidance_floor_shown',
  // instant-connect latency instrumentation (2026-08-31): millisecond
  // timings for the click→pixels chain, the evidence every later
  // pre-warm/prepared-stream graduation rides on (ES-17).
  'boot_gate_passed',
  'session_created',
  'surface_live',
  'intent_warmup',
  // connect-lane routing (2026-09-02): the app has no Composio-managed auth,
  // so the direct mint was never possible — and where the surface sent it.
  'unsupported_auth',
  'browser_lane_routed',
  // non-direct connections P0 (2026-09-02, plan §3.8 hop A / P0 #2): the
  // strategy card posted (detail = research_source: seed | catalog | cache
  // | cold | failed) and the sign-in surface's wrong-destination report.
  'strategy_card_rendered',
  'destination_reported',
  // The paste bridge (2026-09-03): detail is the OUTCOME only — ok |
  // ok-pressed | empty | denied | failed, prefixed `viewer:` when the live
  // view's own paste gesture sent it (2026-09-23, viewer-keys.ts). The
  // clipboard's CONTENT never reaches the funnel, or any other log line; it
  // is credential-shaped by construction.
  'clipboard_pushed',
  // Slice A2 (2026-10-02): the sign-in window's watchdog found the session
  // alive but nothing of the window drawn, and did not call it live. detail =
  // `<sessionId> window-not-drawn | view-not-chosen | screen-not-connected` (the last since slice A3).
  'surface_blank',
  // Slice A3 (2026-10-02): the sign-in window's view ended because the desktop
  // behind it is gone, and the window said "this browser session ended" with
  // "start again". detail = `<sessionId>`.
  'desktop_ended',
  // Slice C1 (2026-10-02): once per window view in the sign-in window, how long the
  // person's input took to show. detail = `n=… key50=… key95=… click95=… delay95=…
  // srv50=… rtt50=…` (milliseconds; numbers only, never a key, a point or a page).
  'view_latency',
  // 2026-10-06 (DocuSign's delivery-method page): presses in the sign-in window changed
  // nothing on the page, and the window asked for its guidance line again. detail =
  // `<page kind> <count>` (a page kind and a number only).
  'guide_reasked',
  // 2026-10-06 (PR 4 of the DocuSign fix): the sign-in window's "open the live desktop" was pressed.
  // detail = `<sessionId> opened | showing | other-machine | popup` (where it went; nothing else).
  'live_desktop_opened',
] as const;

export type ConnectFunnelEvent = (typeof CONNECT_FUNNEL_EVENTS)[number];

/** Every surface the engine attributes a funnel row to (same reason). */
export const CONNECT_FUNNEL_SURFACES = ['chat', 'chat-setup', 'integrations', 'onboarding'] as const;
export type ConnectFunnelSurface = (typeof CONNECT_FUNNEL_SURFACES)[number];

export interface ConnectFunnelRecord {
  event: ConnectFunnelEvent;
  toolkitSlug?: string;
  surface?: string;
  detail?: string;
  /** Milliseconds since the chain's start (instant-connect timing events). */
  ms?: number;
}

type ConnectFunnelSink = (record: ConnectFunnelRecord) => void;

let funnelSink: ConnectFunnelSink = (record) => {
  // Default sink: dev-visible, silent in production. A real analytics sink
  // can replace this via setConnectFunnelSink with NO engine change.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[connect-funnel]', record.event, record);
  }
};

export function setConnectFunnelSink(sink: ConnectFunnelSink | null): void {
  funnelSink = sink || (() => { /* no-op */ });
}

export function logConnectFunnel(record: ConnectFunnelRecord): void {
  try {
    funnelSink(record);
  } catch { /* telemetry must never break the flow */ }
}

// ============================================================================
// Launch (sync popup + mint + navigate)
// ============================================================================

export interface ConnectLaunchOptions {
  toolkitSlug: string;
  /** Human-readable app name for the coach ("Google Drive"); the slug fills
   *  in when omitted. */
  displayName?: string;
  /** 'organization' connects on the workspace identity (server-gated on
   *  integrations.manage); 'personal' or omitted = the caller's own. */
  target?: 'personal' | 'organization';
  /** Setup values some apps need before OAuth (WhatsApp WABA ID, …). */
  initFields?: Record<string, string>;
  /** Additive connect: ids known BEFORE launch, so the new one can be
   *  diffed on return. Writes the add-account localStorage handshake too. */
  addAccount?: { knownIds: string[] };
  /** Explicit auto-share target (2026-08-19 eager-org onboarding): the
   *  storage auto-share lands on this account instead of the caller's
   *  back-pointer workspace. Permission-checked server-side. */
  shareTo?: string;
  popupFeatures?: string;
  /** Telemetry label for the initiating surface ('integrations' | 'chat' |
   *  'onboarding' | …). */
  surface?: string;
  /** Email connect journey (2026-08-20): the inbox the user SAID they are
   *  connecting, captured before OAuth. Post-connect verification compares
   *  the identity that actually signed in against it (mismatch card). Sent
   *  to the verify endpoint only — never into initFields (those forward to
   *  the auth provider). */
  intent?: { intendedAddress?: string };
}

export interface ConnectLaunchResult {
  status: 'navigated' | 'blocked' | 'error' | 'unsupported';
  /** The OAuth window handle when one exists (navigated, or the blocked
   *  retry that succeeded). Surfaces keep it to close on completion. */
  popup: Window | null;
  /** Present on 'navigated' and 'blocked' — the minted OAuth URL. A blocked
   *  surface renders it as <a target="prest0n-connect"> (never blocked). */
  redirectUrl?: string;
  /** Present on 'error' — the raw error, with .code/.fields/.help intact
   *  (missing_fields carries the setup-field metadata). */
  error?: unknown;
  /** Present on 'unsupported' — the lane this app CAN use. 'browser' means
   *  Prest0n signs in on its own computer; 'none' means there is nothing to
   *  sign into. The surface routes accordingly and shows NO error. */
  lane?: ConnectLane;
  /** Present on 'unsupported' when the server named them — the auth schemes
   *  the provider does offer (diagnostics/telemetry only, never user copy). */
  authSchemes?: string[];
}

/**
 * Open the OAuth popup and navigate it to the freshly minted connect URL.
 * MUST be called from inside a user-gesture call stack — window.open runs
 * synchronously before any await.
 */
export async function launchConnectPopup(
  opts: ConnectLaunchOptions
): Promise<ConnectLaunchResult> {
  const features = opts.popupFeatures || CONNECT_POPUP_FEATURES;
  logConnectFunnel({ event: 'started', toolkitSlug: opts.toolkitSlug, surface: opts.surface });

  // ── Lane gate (2026-09-02) ────────────────────────────────────────────
  // Only ~122 of the catalog's ~1,448 toolkits carry Composio-managed auth;
  // for every other one the mint below CANNOT succeed, and opening a popup
  // first is what produced the reported symptom — a sign-in window that
  // appears, vanishes, and leaves an error banner. Read the lane from the
  // catalog copy the client already holds (SYNCHRONOUS by contract — an
  // await here would move window.open out of the user gesture) and hand the
  // surface a clean 'unsupported' verdict instead of a broken popup.
  //
  // 'unknown' (catalog not warm) deliberately PROCEEDS: the server's typed
  // `unsupported_auth` rejection below is the same verdict one round trip
  // later, and a cold catalog must never block an app that would have worked.
  const peekedLane = peekConnectLane(opts.toolkitSlug);
  if (peekedLane !== 'unknown' && !laneIsDirect(peekedLane)) {
    logConnectFunnel({
      event: 'unsupported_auth',
      toolkitSlug: opts.toolkitSlug,
      surface: opts.surface,
      detail: `lane=${peekedLane} (catalog)`,
    });
    return { status: 'unsupported', popup: null, lane: peekedLane };
  }

  // Synchronous open — before ANY await (see module docblock).
  let popup = window.open('', CONNECT_POPUP_WINDOW_NAME, features);
  if (popup) {
    try {
      popup.opener = null;
      popup.document.write(
        '<body style="font-family:sans-serif;color:#555;display:grid;place-items:center;height:95vh;margin:0">opening the sign-in…</body>',
      );
    } catch { /* interim shell is cosmetic */ }
  }

  try {
    // Handshakes BEFORE the mint: the callback page must see a fresh mark
    // even when OAuth completes fast. The surface rides along so the
    // landing's can't-close fallback routes back to where the user started.
    markConnectPopupLaunch(opts.toolkitSlug, opts.surface);
    if (opts.addAccount) {
      markAddAccountLaunch(opts.toolkitSlug, opts.addAccount.knownIds);
    }
    const { redirectUrl } = await connectToolkit(
      opts.toolkitSlug,
      `${window.location.origin}/integrations/callback`,
      {
        ...(opts.target ? { target: opts.target } : {}),
        ...(opts.initFields && Object.keys(opts.initFields).length
          ? { initFields: opts.initFields }
          : {}),
        ...(opts.shareTo ? { shareTo: opts.shareTo } : {}),
      }
    );
    // A mint with no URL is not a connection (2026-09-02): the non-OAuth
    // shapes return no redirect, and navigating the popup to `undefined`
    // parked the user on a broken about:blank that never completed. Treat it
    // as the same unsupported verdict the lane gate gives.
    if (!redirectUrl || !/^https?:\/\//i.test(String(redirectUrl))) {
      try { popup?.close(); } catch { /* best-effort */ }
      logConnectFunnel({
        event: 'unsupported_auth',
        toolkitSlug: opts.toolkitSlug,
        surface: opts.surface,
        detail: 'mint returned no redirect url',
      });
      return { status: 'unsupported', popup: null, lane: 'browser' };
    }
    if (popup && !popup.closed) {
      popup.location.replace(redirectUrl);
      return { status: 'navigated', popup, redirectUrl };
    }
    // Popup blocked (or closed early): one direct retry sometimes lands —
    // the user gesture may still be transient-active.
    popup = window.open(redirectUrl, CONNECT_POPUP_WINDOW_NAME, features);
    if (popup && !popup.closed) {
      return { status: 'navigated', popup, redirectUrl };
    }
    logConnectFunnel({ event: 'popup_blocked', toolkitSlug: opts.toolkitSlug, surface: opts.surface });
    return { status: 'blocked', popup: null, redirectUrl };
  } catch (error) {
    try { popup?.close(); } catch { /* best-effort */ }
    const code = (error as any)?.code;
    // The server saw what a cold client catalog could not: this app has no
    // Composio-managed auth. Same verdict as the lane gate — the surface
    // falls back to the browser lane and the user never sees an error.
    if (code === 'unsupported_auth') {
      const lane = ((error as any)?.lane as ConnectLane) || 'browser';
      const authSchemes = (error as any)?.authSchemes;
      logConnectFunnel({
        event: 'unsupported_auth',
        toolkitSlug: opts.toolkitSlug,
        surface: opts.surface,
        detail: `lane=${lane} (server)`,
      });
      return {
        status: 'unsupported',
        popup: null,
        lane,
        ...(Array.isArray(authSchemes) ? { authSchemes } : {}),
      };
    }
    logConnectFunnel({
      event: code === 'missing_fields' ? 'needs_setup' : 'error',
      toolkitSlug: opts.toolkitSlug,
      surface: opts.surface,
      detail: error instanceof Error ? error.message : String(error),
    });
    return { status: 'error', popup: null, error };
  }
}

// ============================================================================
// Poll (target-aware ACTIVE detection)
// ============================================================================

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface ConnectPollOptions {
  toolkitSlug: string;
  /** Which set to watch. Omitted = the combined legacy list (covers the
   *  server-side storage-defaults-to-organization back-compat). */
  target?: 'personal' | 'organization';
  /** Ids known before launch — the resolved newConnectionId is the diff. */
  knownIds?: string[];
  signal?: AbortSignal;
  intervalMs?: number;
  maxTicks?: number;
}

export interface ConnectPollResult {
  status: 'active' | 'timeout' | 'aborted';
  /** The freshly added connection id when knownIds allowed a diff (or the
   *  only active id on a first connect); null when indeterminate. */
  newConnectionId: string | null;
  /** Every ACTIVE connection id for the slug at resolution time. */
  connectionIds: string[];
  isStorage: boolean;
}

/** One status read of the watched set: ACTIVE connection ids for the slug. */
async function readActiveIds(
  toolkitSlug: string,
  target?: 'personal' | 'organization'
): Promise<string[] | null> {
  const slug = toolkitSlug.toLowerCase();
  try {
    if (!target) {
      const accounts = await getConnectedAccounts();
      const row = accounts.find(
        (a) =>
          (a.toolkit?.slug || '').toLowerCase() === slug &&
          (a.status || '').toUpperCase() === 'ACTIVE'
      );
      if (!row) return [];
      return (row.connections && row.connections.length
        ? row.connections.map((c) => c.id)
        : [row.id]);
    }
    const sets = await getAccountsTwoSets();
    const rows: Array<{ id: string; status: string; toolkit: { slug: string } }> =
      target === 'organization' ? sets.organization || [] : sets.personal || [];
    return rows
      .filter(
        (a) =>
          (a.toolkit?.slug || '').toLowerCase() === slug &&
          (a.status || '').toUpperCase() === 'ACTIVE'
      )
      .map((a) => a.id);
  } catch {
    return null; // transient — keep polling
  }
}

// Test seam: jsdom suites shrink the poll (and the popup-closed watcher) so
// a click never leaves a 2-minute timer dangling. Production never calls this.
let pollDefaultOverrides: {
  intervalMs?: number;
  maxTicks?: number;
  closedWatchIntervalMs?: number;
} | null = null;
export function setConnectPollDefaultsForTests(
  overrides: { intervalMs?: number; maxTicks?: number; closedWatchIntervalMs?: number } | null
): void {
  pollDefaultOverrides = overrides;
}

/** Sleep that ALSO resolves the moment this tab becomes visible again
 *  (2026-08-19 mobile fix): background tabs throttle timers, so the user
 *  finishes OAuth in the popup/tab and comes back to a poll that hasn't
 *  ticked in a minute. Returning to the tab now checks immediately. */
function sleepUntilTickOrVisible(ms: number): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      try { document.removeEventListener('visibilitychange', onVisible); } catch { /* SSR/test env */ }
      clearTimeout(timer);
      resolve();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') finish();
    };
    const timer = setTimeout(finish, ms);
    try { document.addEventListener('visibilitychange', onVisible); } catch { /* SSR/test env */ }
  });
}

export async function pollForActiveConnection(
  opts: ConnectPollOptions
): Promise<ConnectPollResult> {
  const intervalMs = opts.intervalMs ?? pollDefaultOverrides?.intervalMs ?? CONNECT_POLL_INTERVAL_MS;
  const maxTicks = opts.maxTicks ?? pollDefaultOverrides?.maxTicks ?? CONNECT_POLL_MAX_TICKS;
  const storage = supportsFolderScope(opts.toolkitSlug);
  const known = new Set(opts.knownIds || []);

  const check = async (): Promise<ConnectPollResult | null> => {
    const ids = await readActiveIds(opts.toolkitSlug, opts.target);
    if (ids === null) return null;
    // For an additive connect, ACTIVE isn't enough — the NEW id must exist
    // (the roster already had ACTIVE rows before launch).
    const fresh = ids.find((id) => !known.has(id));
    if (opts.knownIds ? !!fresh : ids.length > 0) {
      return {
        status: 'active',
        newConnectionId: fresh ?? (ids.length === 1 ? ids[0] : null),
        connectionIds: ids,
        isStorage: storage,
      };
    }
    return null;
  };

  const deadline = Date.now() + intervalMs * maxTicks;
  for (let tick = 0; tick < maxTicks && Date.now() < deadline; tick++) {
    await sleepUntilTickOrVisible(intervalMs);
    if (opts.signal?.aborted) {
      return { status: 'aborted', newConnectionId: null, connectionIds: [], isStorage: storage };
    }
    const hit = await check();
    if (hit) return hit;
  }
  // One final grace check (2026-08-19): a throttled tab can burn its last
  // tick while OAuth lands — the false "didn't finish connecting" verdict a
  // second before success was worse than one extra read.
  const finalHit = await check().catch(() => null);
  if (finalHit) return finalHit;
  return { status: 'timeout', newConnectionId: null, connectionIds: [], isStorage: storage };
}

// ============================================================================
// Coach events (2026-08-20: one coach ritual on EVERY surface)
// ============================================================================

/** CustomEvent channel for the "the window will say Composio" coach. The
 *  engine dispatches show/hide around the popup lifecycle; the global
 *  ComposioConnectCoachHost (mounted in App.tsx, same pattern as
 *  DesktopSignInModalHost) renders it — so chat-initiated connects get the
 *  same coach the Integrations page always had. */
export const COACH_EVENT = 'prest0n:connect-coach';

export interface ConnectCoachEventDetail {
  phase: 'show' | 'hide';
  /** Per-flow token: a hide only clears the show it belongs to, so an
   *  aborted earlier flow can never hide a newer flow's coach. */
  flowId: number;
  toolkitSlug: string;
  appLabel?: string;
  surface?: string;
  /** Email connect journey (2026-08-20): the inbox the user SAID they are
   *  connecting — the coach pins the sign-in to it ("sign in as X; if a
   *  different account is shown, pick 'use another account'"). */
  intendedAddress?: string;
}

let coachFlowCounter = 0;

function dispatchCoach(detail: ConnectCoachEventDetail): void {
  try {
    window.dispatchEvent(new CustomEvent(COACH_EVENT, { detail }));
  } catch { /* SSR/test env — the coach is cosmetic */ }
}

/** Re-show the coach for the CURRENT (latest) flow — used by [reopen], which
 *  re-opens the sign-in window after the user closed it mid-flow. */
export function reshowConnectCoach(toolkitSlug: string, appLabel?: string, surface?: string): void {
  dispatchCoach({ phase: 'show', flowId: coachFlowCounter, toolkitSlug, appLabel, surface });
}

/**
 * Watch a popup handle and fire onClosed ONCE when the window is gone
 * (~1s cadence — no `close` event exists for an opener-severed window).
 * Returns a cleanup. COOP caveat: some providers detach the handle mid-flow
 * and `closed` flips true early — callers must treat "closed" as cosmetic
 * (hide the coach), NEVER as flow cancellation: the poll must keep running,
 * because users routinely close the window right after consenting.
 */
export function watchPopupClosed(
  popup: Window | null,
  onClosed: () => void,
  intervalMs = 1000
): () => void {
  if (!popup) return () => { /* nothing to watch */ };
  const timer = setInterval(() => {
    let closed = false;
    try { closed = !!popup.closed; } catch { closed = true; }
    if (closed) {
      clearInterval(timer);
      try { onClosed(); } catch { /* observer must never break the flow */ }
    }
  }, intervalMs);
  return () => clearInterval(timer);
}

// ============================================================================
// Full flow (launch + poll + close + cross-surface obligations)
// ============================================================================

export type ConnectPopupOutcome =
  | 'connected'
  | 'timeout'
  | 'denied'
  | 'needs-setup'
  | 'error'
  // The app has no Composio-managed auth (2026-09-02) — the direct sign-in
  // was never possible, so the flow handed it to Prest0n's browser lane.
  // NOT an error: no banner, no failed state, nothing for the user to fix.
  | 'unsupported'
  | 'aborted';

export interface ConnectPopupCallbacks {
  /** The popup is open (or the blocked fallback is showing) — surfaces show
   *  the Composio coach here. Carries the minted URL so a surface can offer
   *  "[reopen]" (same window name → never popup-blocked). */
  onPopupOpened?(info: { redirectUrl: string | null }): void;
  /** Popup blocked even after the retry — render the URL as a link with
   *  target=CONNECT_POPUP_WINDOW_NAME (never blocked). The poll continues:
   *  clicking the link still lands in the same completion path. */
  onPopupBlocked?(info: { toolkitSlug: string; url: string }): void;
  onActive?(info: {
    newConnectionId: string | null;
    connectionIds: string[];
    isStorage: boolean;
  }): void;
  /** Post-connect verification verdict (email journey toolkits only) —
   *  arrives AFTER onActive, from a fire-and-forget probe: the connected
   *  outcome never waits on it, and a failed probe simply never calls this.
   *  `connectionId` (C3b) is the probed connection — what a mismatch remedy
   *  disconnects before relaunching. */
  onVerified?(
    verification: import('./ComposioClient').ConnectionVerification,
    connectionId?: string,
  ): void;
  onDenied?(serverMessage: string): void;
  onNeedsSetup?(fields: ConnectInitField[], help?: ConnectToolkitHelp | null): void;
  onError?(message: string, error?: unknown): void;
  onTimeout?(): void;
  /** No one-click sign-in for this app (2026-09-02). Surfaces that want to
   *  say something in their own idiom implement this; those that don't get
   *  the engine's default — it routes to Prest0n's browser lane itself, so
   *  no surface can leave the user on a dead button. */
  onUnsupported?(info: {
    toolkitSlug: string;
    displayName: string;
    lane: ConnectLane;
    authSchemes?: string[];
  }): void;
}

export function isPermissionDeniedMessage(message: string): boolean {
  return /\b403\b|integrations\.manage|permission|forbidden|not\s+allowed|unauthorized/i.test(
    message
  );
}

export interface RunConnectPopupFlowOptions extends ConnectLaunchOptions {
  signal?: AbortSignal;
  callbacks?: ConnectPopupCallbacks;
  intervalMs?: number;
  maxTicks?: number;
}

/**
 * The complete click-to-connected lifecycle. On ACTIVE this engine (not the
 * surface) closes the popup, invalidates the connected-apps cache, and fires
 * the capability reconcile — every completion path gets the cross-surface
 * obligations exactly once.
 */
export async function runConnectPopupFlow(
  opts: RunConnectPopupFlowOptions
): Promise<ConnectPopupOutcome> {
  const cb = opts.callbacks || {};
  const launch = await launchConnectPopup(opts);

  // No managed auth (2026-09-02). The engine is the choke point EVERY surface
  // goes through, so the never-a-dead-end guarantee lives here: a surface
  // that implements onUnsupported owns the hand-off, and one that doesn't
  // still gets the browser lane opened for it rather than a stuck button.
  if (launch.status === 'unsupported') {
    const displayName = opts.displayName || opts.toolkitSlug;
    const lane = launch.lane || 'browser';
    if (cb.onUnsupported) {
      cb.onUnsupported({
        toolkitSlug: opts.toolkitSlug,
        displayName,
        lane,
        ...(launch.authSchemes ? { authSchemes: launch.authSchemes } : {}),
      });
    } else if (lane === 'browser') {
      const { routeToBrowserLane } = await import('./connectLane');
      await routeToBrowserLane({
        appName: displayName,
        toolkitSlug: opts.toolkitSlug,
        ...(opts.surface ? { surface: opts.surface } : {}),
      });
    }
    return 'unsupported';
  }

  if (launch.status === 'error') {
    const error = launch.error;
    const message = error instanceof Error ? error.message : String(error);
    if ((error as any)?.code === 'missing_fields' && Array.isArray((error as any)?.fields)) {
      cb.onNeedsSetup?.((error as any).fields, (error as any).help || null);
      return 'needs-setup';
    }
    if (isPermissionDeniedMessage(message)) {
      logConnectFunnel({ event: 'denied', toolkitSlug: opts.toolkitSlug, surface: opts.surface });
      cb.onDenied?.(message);
      return 'denied';
    }
    cb.onError?.(message, error);
    return 'error';
  }

  // Coach ritual (2026-08-20): show when the sign-in window opens, hide the
  // moment that window is gone or the flow resolves. The window closing
  // hides ONLY the coach — never the poll: users routinely close the window
  // right after consenting (and the callback landing self-closes it), and
  // the connection still reports ACTIVE a beat later.
  const coachFlowId = ++coachFlowCounter;
  const coachDetail = {
    flowId: coachFlowId,
    toolkitSlug: opts.toolkitSlug,
    appLabel: opts.displayName,
    surface: opts.surface,
    ...(opts.intent?.intendedAddress ? { intendedAddress: opts.intent.intendedAddress } : {}),
  };
  dispatchCoach({ phase: 'show', ...coachDetail });
  const stopClosedWatch = watchPopupClosed(
    launch.popup,
    () => { dispatchCoach({ phase: 'hide', ...coachDetail }); },
    pollDefaultOverrides?.closedWatchIntervalMs
  );
  const retireCoach = () => {
    stopClosedWatch();
    dispatchCoach({ phase: 'hide', ...coachDetail });
  };

  cb.onPopupOpened?.({ redirectUrl: launch.redirectUrl || null });
  if (launch.status === 'blocked' && launch.redirectUrl) {
    cb.onPopupBlocked?.({ toolkitSlug: opts.toolkitSlug, url: launch.redirectUrl });
  }

  const poll = await pollForActiveConnection({
    toolkitSlug: opts.toolkitSlug,
    target: opts.target,
    knownIds: opts.addAccount?.knownIds,
    signal: opts.signal,
    intervalMs: opts.intervalMs,
    maxTicks: opts.maxTicks,
  });
  retireCoach();

  if (poll.status === 'active') {
    // The OAuth window's job is done the moment the connection reports
    // ACTIVE — close it from here (the callback landing's self-close is the
    // other belt).
    try {
      if (launch.popup && !launch.popup.closed) launch.popup.close();
    } catch { /* popup may already be gone */ }
    // Cross-surface obligations, centralized: chat grounding stays fresh and
    // the assistant's capability flags flip live on the very next turn.
    invalidateConnectedAppsCache();
    void import('../prest0nSession/client')
      .then((m) => m.reconcilePrest0nConnections())
      .catch(() => { /* best-effort — the portal poll reconcile is the backstop */ });
    logConnectFunnel({ event: 'connected', toolkitSlug: opts.toolkitSlug, surface: opts.surface });
    cb.onActive?.({
      newConnectionId: poll.newConnectionId,
      connectionIds: poll.connectionIds,
      isStorage: poll.isStorage,
    });
    // Post-connect verification (2026-08-20 connect-journey batch): learn WHO
    // actually signed in + mailbox health the moment the connection is
    // ACTIVE. Fire-and-forget — the connected outcome above never waits, and
    // the server persists the verdict even if this tab dies mid-probe (the
    // reconcile backfill is the belt to this suspender).
    const verifyTarget = poll.newConnectionId || poll.connectionIds[0] || null;
    // 2026-08-22: every toolkit verifies (was email-journeys only — the
    // reason only Gmail/Outlook tiles ever named their account). Non-journey
    // toolkits resolve identity through the server's retrieve+extractor lane.
    // An app Prest0n connects through its own OAuth client (Upwork) is not Composio's to verify.
    if (verifyTarget && !firstPartyAppOf(opts.toolkitSlug)) {
      void import('./ComposioClient')
        .then((m) => m.verifyConnection(verifyTarget, opts.toolkitSlug, opts.intent?.intendedAddress))
        .then((verification) => {
          if (verification) cb.onVerified?.(verification, verifyTarget);
        })
        .catch(() => { /* verification is an enhancement, never an error */ });
    }
    return 'connected';
  }

  if (poll.status === 'aborted') {
    logConnectFunnel({ event: 'aborted', toolkitSlug: opts.toolkitSlug, surface: opts.surface });
    return 'aborted';
  }

  logConnectFunnel({ event: 'timeout', toolkitSlug: opts.toolkitSlug, surface: opts.surface });
  cb.onTimeout?.();
  return 'timeout';
}
